import { Injectable } from '@angular/core'
import { Action, Selector, State, StateContext } from '@ngxs/store'
import { SelbstzahlerDashboardReq } from '../../model/inko-backend.model'
import { DashboardZeitraeume, mitAktuellenZeitraeumen } from '../../util/dashboard-filter'

/** Der Filter samt der Marken mitlaufender Zeitraeume (`DashboardZeitraeume`). */
export type SelbstzahlerDashboardFilter = SelbstzahlerDashboardReq & DashboardZeitraeume

/* Model */

export interface SelbstzahlerDashboardStateModel {
  selbstzahlerFilter: SelbstzahlerDashboardFilter
  /**
   * Schluessel der fest eingebauten Kacheln, die der Nutzer entfernt hat. Bewusst eine Liste der
   * ENTFERNTEN statt der sichtbaren: so erreichen spaeter ergaenzte Preset-Kacheln auch Nutzer,
   * die ihre Kacheln schon einmal angepasst haben. Ein `key` wird deshalb nie wiederverwendet.
   */
  selbstzahlerDismissedPresets?: string[]
  selbstzahlerKpiCards: {
    title: string
    selbstzahlerFilter: SelbstzahlerDashboardFilter
  }[]
}

/* Actions */

export class DashboardStateSelbstzahlerSetFilter {
  static readonly type = '[Dashboard] Set Selbstzahler Filter'
  constructor(public filter: Partial<SelbstzahlerDashboardFilter>) {}
}

export class DashboardStateSelbstzahlerResetFilter {
  static readonly type = '[Dashboard] Reset Selbstzahler Filter'
}

export class DashboardStateSelbstzahlerAddKpiCard {
  static readonly type = '[Dashboard] Add Selbstzahler KPI Card'
  constructor(
    public title: string,
    public filter: SelbstzahlerDashboardFilter,
  ) {}
}

export class DashboardStateSelbstzahlerRemoveKpiCard {
  static readonly type = '[Dashboard] Remove Selbstzahler KPI Card'
  constructor(public index: number) {}
}

export class DashboardStateSelbstzahlerDismissPresetKpiCard {
  static readonly type = '[Dashboard] Dismiss Selbstzahler Preset KPI Card'
  constructor(public key: string) {}
}

export class DashboardStateSelbstzahlerRestorePresetKpiCards {
  static readonly type = '[Dashboard] Restore Selbstzahler Preset KPI Cards'
}

/* State */

/**
 * Der Filter ohne jede Einschraenkung. Er ist NICHT die Startlage (siehe `defaultState`), sondern
 * das Ziel von „Filter entfernen" und von jedem Kachel-Klick — die Kachel setzt erst zurueck und
 * legt dann ihren eigenen Filter auf. Stuende die Startlage hier, koennte die Kachel „Alle
 * Verkaeufe" ihren leeren Filter gar nicht durchsetzen: der vorbelegte Status bliebe stehen.
 */
const leererFilter: SelbstzahlerDashboardFilter = {
  pageSize: 30,
}

/**
 * Startlage: die laufenden Verkaeufe. Beendete sind Archiv und wuerden die Liste mit der Zeit
 * zuwachsen lassen. Anders als bei Hilfsmitteln und Pflegehilfsmitteln steht der Status damit
 * vorbelegt im Filter — sichtbar als Chip, also mit einem Klick abzuwerfen.
 */
const defaultState: SelbstzahlerDashboardStateModel = {
  selbstzahlerFilter: {
    ...leererFilter,
    versorgungsStatus: ['AKTIV'],
  },
  selbstzahlerDismissedPresets: [],
  selbstzahlerKpiCards: [],
}

@State<SelbstzahlerDashboardStateModel>({
  name: 'selbstzahlerDashboard',
  defaults: defaultState,
})
@Injectable()
export class SelbstzahlerDashboardState {
  @Selector()
  static selbstzahlerFilter(state: SelbstzahlerDashboardStateModel) {
    // Mitlaufende Zeitraeume („Letzter Monat“) auf heute ausrechnen — der persistierte Stand kann von gestern sein.
    return mitAktuellenZeitraeumen(state.selbstzahlerFilter)
  }

  @Selector()
  static selbstzahlerKpiCards(state: SelbstzahlerDashboardStateModel) {
    return state.selbstzahlerKpiCards
  }

  @Selector()
  static selbstzahlerDismissedPresets(state: SelbstzahlerDashboardStateModel): string[] {
    // Persistierter Altstand kennt das Feld nicht — ohne Fallback waere es undefined.
    return state.selbstzahlerDismissedPresets ?? []
  }

  @Selector()
  static selbstzahlerFilterCounter(state: SelbstzahlerDashboardStateModel) {
    const filter = state.selbstzahlerFilter
    return (
      (filter.versorgungsStatus?.length ?? 0) +
      (filter.externalPartnerIds?.length ?? 0) +
      (filter.deliveryInterval ? 1 : 0) +
      (typeof filter.vomCallcenterAngelegt === 'boolean' ? 1 : 0) +
      (filter.fromCreatedDate || filter.toCreatedDate ? 1 : 0)
    )
  }

  @Action(DashboardStateSelbstzahlerSetFilter)
  setSelbstzahlerFilter(ctx: StateContext<SelbstzahlerDashboardStateModel>, action: DashboardStateSelbstzahlerSetFilter) {
    ctx.patchState({
      selbstzahlerFilter: {
        ...ctx.getState().selbstzahlerFilter,
        ...action.filter,
      },
    })
  }

  @Action(DashboardStateSelbstzahlerResetFilter)
  resetSelbstzahlerFilter(ctx: StateContext<SelbstzahlerDashboardStateModel>) {
    ctx.patchState({
      selbstzahlerFilter: leererFilter,
    })
  }

  @Action(DashboardStateSelbstzahlerAddKpiCard)
  addSelbstzahlerKpiCard(ctx: StateContext<SelbstzahlerDashboardStateModel>, action: DashboardStateSelbstzahlerAddKpiCard) {
    ctx.patchState({
      selbstzahlerKpiCards: [
        ...ctx.getState().selbstzahlerKpiCards,
        {
          title: action.title,
          selbstzahlerFilter: action.filter,
        },
      ],
    })
  }

  @Action(DashboardStateSelbstzahlerRemoveKpiCard)
  removeSelbstzahlerKpiCard(ctx: StateContext<SelbstzahlerDashboardStateModel>, action: DashboardStateSelbstzahlerRemoveKpiCard) {
    const currentCards = ctx.getState().selbstzahlerKpiCards.slice()
    currentCards.splice(action.index, 1)
    ctx.patchState({
      selbstzahlerKpiCards: currentCards,
    })
  }

  @Action(DashboardStateSelbstzahlerDismissPresetKpiCard)
  dismissSelbstzahlerPresetKpiCard(
    ctx: StateContext<SelbstzahlerDashboardStateModel>,
    action: DashboardStateSelbstzahlerDismissPresetKpiCard,
  ) {
    const dismissed = ctx.getState().selbstzahlerDismissedPresets ?? []
    if (dismissed.includes(action.key)) {
      return
    }
    ctx.patchState({ selbstzahlerDismissedPresets: [...dismissed, action.key] })
  }

  @Action(DashboardStateSelbstzahlerRestorePresetKpiCards)
  restoreSelbstzahlerPresetKpiCards(ctx: StateContext<SelbstzahlerDashboardStateModel>) {
    // Nur die Presets kommen zurueck; selbst angelegte Kacheln bleiben unangetastet.
    ctx.patchState({ selbstzahlerDismissedPresets: [] })
  }
}
